import { locationTaxonomy } from './location_taxonomy.js';
import { Picture } from './picture.js';

/**
 * Represents a location interface around the location data.
 */
const LocationBaseMixin = Base =>
  class LocationBase extends Base {
    constructor(data, getLocationFrom) {
      super();
      this.data = data;
      this.#getLocationFrom = getLocationFrom;
    }

    get id() {
      return this.data.id;
    }

    get notFound() {
      return this.data.notFound;
    }

    get name() {
      return this.data.name || '';
    }

    // Returns name with subtype appended at the end if the name doesn't include
    // any of subtype words.
    get extendedName() {
      if (!this.subtype) {
        return this.name;
      }
      let words = this.subtype
        .split(' ')
        .filter(word => !!word)
        .map(word => word.toLowerCase());
      if (this.type == 'waterbody') {
        // French support: 'Antostagan Lake' or 'Lac Antostagan' in French.
        words.push('lac');
      }
      if (
        words.length > 0 &&
        !words.some(word => this.name.toLowerCase().split(' ').includes(word))
      ) {
        return `${this.name} ${this.subtype}`;
      }
      return this.name;
    }

    // Returns the location extended name plus its toponym,
    // for example, 'Canoe Lake, The Massasauga'.
    get fullName() {
      let toponymPostfix = '';
      let { toponym } = this;
      if (toponym) {
        toponymPostfix = `, ${toponym.name}`;
      }
      return `${this.extendedName}${toponymPostfix}`;
    }

    // Returns the webpage title.
    get title() {
      return `${this.fullName} | CamperAid`;
    }

    get type() {
      return this.data.type || 'toponym';
    }

    get subtype() {
      return this.data.subtype || '';
    }

    get typeName() {
      return (
        this.data.subtype ||
        (this.data.type != 'toponym' && this.typeInfo.name) ||
        ''
      );
    }

    get google_id() {
      return this.data.google_id ?? '';
    }

    get lat() {
      return parseFloat(this.data.lat);
    }

    get lng() {
      return parseFloat(this.data.lng);
    }

    get position() {
      return {
        lat: this.lat,
        lng: this.lng,
      };
    }

    get routes() {
      return this.data.routes ?? [];
    }

    get availability() {
      return {
        lastUpdateDate: this.data.availability?.lastUpdateDate
          ? new Date(this.data.availability.lastUpdateDate)
          : null,
        calendar: this.data.availability?.calendar?.map(
          month => month?.days ?? []
        ),
      };
    }

    get calendar() {
      let { lastUpdateDate, calendar } = this.availability;
      if (!lastUpdateDate || !calendar) {
        return null;
      }

      // Calendar should be of this month.
      let hasAvailabilityData =
        new Date().getFullYear() == lastUpdateDate.getFullYear() &&
        new Date().getMonth() == lastUpdateDate.getMonth();
      return hasAvailabilityData ? calendar : null;
    }

    get history() {
      return this.data.history ?? [];
    }

    get readonly() {
      return this.data.readonly;
    }

    get moderationWanted() {
      return parseInt(this.data.moderation_wanted);
    }

    // Returns main provider name which supplies the location info.
    get provider() {
      return this.data.provider;
    }

    // Returns all related provider types associated with the location.
    get providers() {
      return this.data.providers;
    }

    get googleMapsUrl() {
      return this.data.google_id
        ? `https://www.google.com/maps/place/?q=place_id:${this.data.google_id}`
        : `https://www.google.com/maps/place/${this.data.lat},${this.data.lng}/@${this.data.lat},${this.data.lng},8z`;
    }

    // Details

    get description() {
      return this.data.description || '';
    }

    get fullDescription() {
      let headline = '';
      let { description, type } = this;
      switch (type) {
        case 'campsite': {
          let { waterbody, toponym } = this;
          if (!toponym && !waterbody) {
            break;
          }
          let toponymName = (toponym && toponym.extendedName) || '';
          let waterbodyName = (waterbody && waterbody.extendedName) || '';

          let preposition = 'on';
          if (waterbody) {
            toponymName = `, ${toponymName}`;
          } else {
            preposition = 'in';
          }
          headline = `The campsite is located ${preposition} ${waterbodyName}${toponymName}.`;
          break;
        }

        case 'portage': {
          let { waterbodies } = this;
          if (waterbodies.length == 2) {
            headline = `The portage connects ${waterbodies[0].extendedName} and ${waterbodies[1].extendedName}.`;
          }
          break;
        }

        case 'waterbody': {
          let { name, toponym } = this;
          headline = toponym
            ? `${name} is located in ${toponym.extendedName}.`
            : '';
          break;
        }

        default:
          break;
      }

      if (!headline) {
        return description;
      }
      return (description && `${headline} ${description}`) || headline;
    }

    get attributes() {
      let attributes = [];
      for (let attrName in this.typeInfo.attributes || {}) {
        let attrInfo = this.typeInfo.attributes[attrName];
        let values = this.attributesOf(attrName);
        if (attrInfo && values && values.length > 0) {
          attributes.push({
            name: attrName,
            title: attrInfo.title,
            values,
          });
        }
      }
      return attributes;
    }

    attributesOf(name) {
      return this.data.attributes?.[name] ?? [];
    }

    get fields() {
      const Fields = this.constructor.Fields;
      const SortingList = this.constructor.FieldsSortingList;

      if (Array.from(Object.keys(Fields)).length != SortingList.length) {
        throw new Error(
          `LocationBaseMixin.Fields and LocationBaseMixin.FieldsSortingList are out of sync`
        );
      }

      return this.data.fields
        ? [...this.data.fields].sort((a, b) => {
            if (SortingList.indexOf(a[0]) < SortingList.indexOf(b[0])) {
              return -1;
            }
            if (SortingList.indexOf(a[0]) > SortingList.indexOf(b[0])) {
              return 1;
            }
            return 0;
          })
        : [];
    }

    get typedFields() {
      return this.fields.map(([key, value]) => ({
        name: key,
        type: this.constructor.FieldsInfo.get(key),
        value,
      }));
    }

    get state() {
      return this.data.state;
    }

    get cover() {
      return this.data.cover;
    }

    get pics() {
      return this.data.pics || [];
    }

    get picsInfo() {
      return this.data.picsInfo;
    }

    get rels() {
      return (
        (this.data.rels &&
          this.data.rels.map(relData => this.#getLocationFrom(relData))) ||
        []
      );
    }

    get toponym() {
      return this.rels.find(rel => rel.type == 'toponym');
    }

    get waterbody() {
      return this.rels.find(rel => rel.type == 'waterbody');
    }

    get waterbodies() {
      return this.rels.filter(rel => rel.type == 'waterbody');
    }

    get overallRating() {
      return this.reviews.overallRating;
    }

    get summary() {
      return this.reviews.summary;
    }

    get reviews() {
      if (!this.#reviews) {
        this.#reviews = new this.constructor.Reviews(this);
      }
      return this.#reviews;
    }

    refreshReviews() {
      this.#reviews?.setReviews(this.data.reviews ?? []);
    }

    get coverUrl() {
      return (
        (this.cover && Picture.getPicUrl(this.cover)) || Picture.defaultPic
      );
    }

    get midsizeCoverUrl() {
      return (
        (this.cover && Picture.getMidSizePicUrl(this.cover)) ||
        Picture.defaultPic
      );
    }

    get listPics() {
      // Pics for image list only if more than one pic.
      return this.pics.length > 1 ? Picture.toThumbnailUrls(this.pics) : [];
    }

    get mosaicPics() {
      return (
        (this.picsInfo &&
          this.picsInfo.length > 0 &&
          this.picsInfo.map((pic, idx) => ({
            url:
              (idx <= 1 && Picture.getMidSizePicUrl(pic.id)) ||
              Picture.getPicUrl(pic.id, 320),
            alt: this.getPicAlt(pic.description),
          }))) || [
          {
            url: Picture.defaultPic,
            alt: '',
          },
        ]
      );
    }

    hasPics() {
      return this.pics && this.pics.length > 0;
    }

    getPicAlt(description) {
      let alt = this.name;
      if (description) {
        alt = this.name ? `${this.name}. ${description}` : description;
      }
      return alt;
    }

    // Type info
    get typeInfo() {
      if (!this.#typeInfo) {
        this.#typeInfo = locationTaxonomy.get(this);
      }
      return this.#typeInfo;
    }

    get ratingInfo() {
      return this.typeInfo.ratings || [];
    }

    ratingInfoById(ratingId) {
      for (let ratingGroup of this.ratingInfo) {
        for (let ratingItem of ratingGroup.items || []) {
          if (ratingItem.id == ratingId) {
            return ratingItem;
          }
        }
      }
      return null;
    }

    get zoom() {
      return this.typeInfo.zoom;
    }

    typeChanged() {
      this.#typeInfo = null;
    }

    static Fields = {
      Reservation: 'Reservation',
      BackcountryReservation: 'Backcountry Reservation',
      Registration: 'Registration',
      RegistrationAndFees: 'Registration and fees',

      Address: 'Address',
      Email: 'Email',
      Phone: 'Phone',

      Instagram: 'Instagram',
      Facebook: 'Facebook',
      Flickr: 'Flickr',
      Youtube: 'Youtube',
      Pinterest: 'Pinterest',
      TripAdvisor: 'TripAdvisor',
      Twitter: 'Twitter',

      FriendsOf: 'Friends of ',
      Website: 'Website',

      JeffMaps: `Jeff's maps`,
      UnlostifyMaps: `Unlostify maps`,
      ChrismarMaps: `Chrismar maps`,
    };

    static FieldTypes = {
      Registration: 'Registration',
      Address: 'Address',
      Email: 'Email',
      Phone: 'Phone',
      SocialMedia: 'SocialMedia',
      Website: 'Website',
    };

    static FieldsInfo = new Map([
      [this.Fields.Reservation, this.FieldTypes.Registration],
      [this.Fields.BackcountryReservation, this.FieldTypes.Registration],
      [this.Fields.Registration, this.FieldTypes.Registration],
      [this.Fields.RegistrationAndFees, this.FieldTypes.Registration],

      [this.Fields.Address, this.FieldTypes.Address],
      [this.Fields.Email, this.FieldTypes.Email],
      [this.Fields.Phone, this.FieldTypes.Phone],

      [this.Fields.Instagram, this.FieldTypes.SocialMedia],
      [this.Fields.Facebook, this.FieldTypes.SocialMedia],
      [this.Fields.Flickr, this.FieldTypes.SocialMedia],
      [this.Fields.Youtube, this.FieldTypes.SocialMedia],
      [this.Fields.Pinterest, this.FieldTypes.SocialMedia],
      [this.Fields.TripAdvisor, this.FieldTypes.SocialMedia],
      [this.Fields.Twitter, this.FieldTypes.SocialMedia],

      [this.Fields.FriendsOf, this.FieldTypes.Website],
      [this.Fields.Website, this.FieldTypes.Website],

      [this.Fields.JeffMaps, this.FieldTypes.Website],
      [this.Fields.UnlostifyMaps, this.FieldTypes.Website],
      [this.Fields.ChrismarMaps, this.FieldTypes.Website],
    ]);

    static FieldsSortingList = [
      this.Fields.Website,
      this.Fields.Reservation,
      this.Fields.BackcountryReservation,
      this.Fields.Registration,
      this.Fields.RegistrationAndFees,
      this.Fields.FriendsOf,
      this.Fields.JeffMaps,
      this.Fields.UnlostifyMaps,
      this.Fields.ChrismarMaps,
      this.Fields.Phone,
      this.Fields.Email,
      this.Fields.Address,
      this.Fields.Facebook,
      this.Fields.Flickr,
      this.Fields.Youtube,
      this.Fields.Instagram,
      this.Fields.Pinterest,
      this.Fields.TripAdvisor,
      this.Fields.Twitter,
    ];

    static get Reviews() {
      throw new Error(`[data] 'Reviews' must be overriden in a derived class`);
    }

    #getLocationFrom;
    #reviews;
    #typeInfo;
  };

export { LocationBaseMixin };
